import { useCallback, useEffect, useState, forwardRef, useImperativeHandle } from 'react';

import { api } from '../api.js';
import { refreshNavCounts } from '../hooks/useNavCounts.js';
import Row from './UnplacedMailCard.jsx';

// T392 — mail that is waiting on a decision, shown where he works.
//
// He asked why a confirmation "nu e asociat si nu e deschis task". Everything
// needed to place it existed — chips, a typed picker, "I applied here but it
// is not in the tracker" — on the MAIL page, so it was never work he was
// told about. Twelve messages sat there.
//
// Measured before building this: the matcher reaches 93% and the remaining
// 12 are not rule failures. One refers to a job that was never imported, so
// there is nothing to link it to; the rest name a company where he has
// several applications and name no role, so the rules refuse rather than
// guess. Both are questions, and a question belongs in Tasks.

const UnplacedMail = forwardRef(function UnplacedMail({ onCount }, ref) {
  const [emails, setEmails] = useState(null);
  const [candidatures, setCandidatures] = useState([]);
  const [failed, setFailed] = useState(false);
  const [total, setTotal] = useState(0);
  const [truncated, setTruncated] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/emails?unmatched=1&limit=200');
      setEmails(r.emails ?? []);
      setCandidatures(r.candidatures ?? []);
      setTotal(r.total ?? r.emails?.length ?? 0);
      setTruncated(Boolean(r.truncated));
      setFailed(false);
      // T408 — the badge counts the whole queue, so the count reported here
      // has to be the same number or the two disagree on screen.
      onCount?.(r.total ?? r.emails?.length ?? 0);
    } catch {
      setEmails([]);
      setFailed(true);
    }
  }, [onCount]);

  useImperativeHandle(ref, () => ({ reload: load }), [load]);
  useEffect(() => {
    load();
  }, [load]);

  const drop = (id) =>
    setEmails((prev) => {
      const next = (prev ?? []).filter((e) => e._id !== id);
      onCount?.(next.length);
      // The sidebar counts unlinked mail too, and a badge that lags after the
      // thing it counts is gone teaches him it lies (T387).
      refreshNavCounts();
      return next;
    });

  if (!emails) return null;
  if (emails.length === 0) {
    return (
      <section className="space-y-2">
        <h2 className="text-sm font-medium text-brand-500">Mail waiting to be placed</h2>
        <p className="max-w-prose text-xs text-slate-500">
          {failed
            ? 'Could not load these just now — try Fetch mail again.'
            : 'Nothing unplaced. Replies the rules cannot attach on their own will appear here.'}
        </p>
      </section>
    );
  }

  return (
    <section className="space-y-2">
      <h2 className="text-sm font-medium text-brand-500">Mail waiting to be placed</h2>
      <p className="max-w-prose text-xs text-slate-500">
        Each of these names a company but not which role, or a job hunter has never seen. The rules
        refuse to guess between them, so they are yours to place.{' '}
        {/* T412 — this sentence was on the Mail page and nearly went down
            with it. It is the distinction the whole feature rests on:
            linking records what a message is ABOUT, it does not decide that
            an application advanced. The stage change arrives separately, as
            something to approve. */}
        Linking a message says it belongs to that application; it does not move the application&rsquo;s
        stage.
      </p>
      {truncated && (
        <p className="max-w-prose text-xs text-amber-400">
          Showing the {emails.length} most recent of {total}. Place some and the rest appear.
        </p>
      )}
      <ul className="space-y-2">
        {/* Row renders its own <li> — it was lifted out of a list on the Mail
            page and kept that shape, so wrapping it in another one here would
            nest <li> inside <li>. */}
        {emails.map((e) => (
          <Row key={e._id} email={e} candidatures={candidatures} onLinked={drop} onDeleted={drop} />
        ))}
      </ul>
    </section>
  );
});

export default UnplacedMail;
